import type {
  AmlHit,
  AmlReview,
  AmlReviewExternalReference,
  AmlReviewResult,
  AmlReviewResultVerdict,
} from '@/integrations/Immersve/aml-review/types';
import {
  formatDate,
  fullName,
  type CaseDetail,
  type CaseHit,
  type CaseIdentityDetails,
  type HitVerdict,
  type IdentityFieldDiff,
  type RawVerdictStatus,
} from './caseDetail';
import type { HitEnrichment } from './resolveHitEnrichment';

// FrankieOne's portal host is the real (non-uat) one only when Larimar's own
// deploy is prod AND the case itself is live — prod-but-test and any dev case
// (live or test) all use the uat host, since a dev deploy's FrankieOne
// credentials are the sandbox tenant regardless of the case's own liveness.
// Sumsub's host never varies by either and always deep-links to watchlists.
const providerCaseUrlBuilders: Record<
  'frankieOne' | 'sumsub',
  (externalId: string, env: string, testModeEnabled: boolean) => string
> = {
  frankieOne: (id, env, testModeEnabled) => {
    const isLive = env === 'prod' && !testModeEnabled;
    return `https://portal.kycaml.${isLive ? '' : 'uat.'}frankiefinancial.io/entities/profile/${id}/pep-sanctions`;
  },
  sumsub: (id) => `https://cockpit.sumsub.com/checkus/applicant/${id}/basicInfo?anchor=watchlists`,
};

function resolveZendeskCaseUrl(
  externalReferences?: AmlReviewExternalReference[]
): string | undefined {
  return externalReferences?.find((ref) => ref.resource === 'Zendesk')?.url;
}

function reviewStatusFromAmlReviewStatus(status: string): 'needs_review' | 'done' {
  return status === 'complete' ? 'done' : 'needs_review';
}

// The real verdict enum has five values; potential_match/no_match are
// Sumsub-only and collapse to unknown for display, per the 2026-07-28
// "verdict presentation models the full domain vocabulary" decision
// (aml-review-console/decisions.md).
function collapseVerdict(verdict: string): HitVerdict {
  if (verdict === 'false_positive') return 'false_positive';
  if (verdict === 'true_positive') return 'true_positive';
  return 'unknown';
}

// matchedName has no real backend field anywhere (see
// aml-review-console/decisions.md, 2026-07-31) — no per-hit source exists
// to draw a name from, on either provider. A constant, honest placeholder
// beats cycling fixture names (which presented invented identity data as
// fact) or a raw hit id (which reads as a technical string, not a name).
// `records` stays unset: neither provider's structured record data crosses
// the API boundary yet (see this project's own decisions.md, "Rejected:
// per-record cmp fields").
export const MATCHED_NAME_UNAVAILABLE = 'Unknown match';

export function resolveIdentityDetails(review: AmlReview): CaseIdentityDetails {
  const nameParts = review.identityDetails?.name;
  return {
    name: {
      givenName: nameParts?.givenName ?? '',
      middleName: nameParts?.middleName,
      familyName: nameParts?.familyName ?? '',
    },
    dateOfBirth: review.identityDetails?.dateOfBirth ?? '',
    address: review.identityDetails?.address ?? '',
  };
}

const NOT_SUPPLIED = 'Not supplied';

// The supplied side of the identity comparison is real — review.identityDetails
// is the same contract CaseInfo's own fields use. The matched side has no
// real source (no matched identity field exists on AmlHit or
// AmlReviewResultVerdict), so it's left explicitly absent (`—`, `unknown`)
// rather than invented — the same shape IdentityComparison.vue already
// renders for a field the fixture doesn't have. Identical across every hit
// on this review, since there is nothing per-hit to vary it by.
function buildIdentityDiff(identityDetails: CaseIdentityDetails): IdentityFieldDiff[] {
  const supplied = {
    'Full name': fullName(identityDetails.name) || NOT_SUPPLIED,
    'Date of birth': identityDetails.dateOfBirth
      ? formatDate(identityDetails.dateOfBirth)
      : NOT_SUPPLIED,
    Address: identityDetails.address || NOT_SUPPLIED,
  };
  return Object.entries(supplied).map(([field, subject]) => ({
    field,
    subject,
    matched: '—',
    status: 'unknown',
  }));
}

function mapHit(
  id: string,
  amlHit: AmlHit | undefined,
  verdict: AmlReviewResultVerdict | undefined,
  diff: IdentityFieldDiff[],
  enrichment: HitEnrichment | undefined
): CaseHit {
  const rawStatus = (verdict?.verdict ?? amlHit?.status ?? 'unknown') as RawVerdictStatus;
  return {
    id,
    matchedName: enrichment?.matchedName || MATCHED_NAME_UNAVAILABLE,
    verdict: collapseVerdict(rawStatus),
    rawStatus,
    hitTypes: amlHit?.hitTypes ?? [],
    whitelisted: verdict?.whitelisted,
    botReasoning: verdict?.reasoning,
    evidence: verdict?.evidence,
    diff: enrichment?.diff ?? diff,
    records: enrichment?.records,
  };
}

export function mapAmlReviewToCase(
  review: AmlReview,
  result: AmlReviewResult | null,
  hitsById?: Map<string, HitEnrichment>,
  { env = 'local', testModeEnabled = false }: { env?: string; testModeEnabled?: boolean } = {}
): CaseDetail {
  const amlHitsById = new Map((review.amlHits ?? []).map((hit) => [hit.id, hit]));
  const verdictsById = new Map((result?.verdicts ?? []).map((verdict) => [verdict.hitId, verdict]));
  const orderedIds = [
    ...(review.amlHits ?? []).map((hit) => hit.id),
    ...[...verdictsById.keys()].filter((id) => !amlHitsById.has(id)),
  ];

  const identityDetails = resolveIdentityDetails(review);
  const diff = buildIdentityDiff(identityDetails);

  return {
    amlReviewId: review.amlReviewId,
    externalProfileId: review.externalProfileId,
    externalCheckId: review.externalCheckId ?? '',
    accountId: review.accountId,
    partnerName: review.partnerName,
    kycProvider: review.kycProvider,
    region: review.region,
    reviewStatus: reviewStatusFromAmlReviewStatus(review.status),
    providerCaseUrl: providerCaseUrlBuilders[review.kycProvider](
      review.externalProfileId,
      env,
      testModeEnabled
    ),
    zendeskCaseUrl: resolveZendeskCaseUrl(review.externalReferences),
    supersedingAmlReviewId: review.supersedingAmlReviewId,
    createdAt: review.createdAt,
    identityDetails,
    recommendation: result?.recommendation,
    summary: result?.summary,
    hits: orderedIds.map((id) =>
      mapHit(id, amlHitsById.get(id), verdictsById.get(id), diff, hitsById?.get(id))
    ),
  };
}
